import { Container } from "../../components/Container/Container";
import styled from "styled-components";
import { Body, Headline2 } from "../../components/Text/Text";
import { ButtonLink, UnstyledButtonLink } from "../../components/ButtonLink/ButtonLink";
import { useAuth } from "../../contexts/auth-context";
import { submitLoginForm } from "../../actions/submit-login-form";
import { useActionState, useEffect, useState } from "react";
import { Spinner } from "../../components/Spinner/Spinner";
import { Button } from "../../components/Button/Button";

const Background = styled.div`
    background-color: var(--color-light);
`;

const StyledContainer = styled(Container)`
    padding-top: var(--menu-height);
`;

const StyledHeadline2 = styled(Headline2)`
    text-transform: uppercase;
    margin-bottom: 20px;
`;

const Flex = styled.div`
    display: flex;
    flex-direction: column;
    align-items: start;
    max-width: 544px;
    
    margin: 0 auto;
    padding-top: 150px;
    padding-bottom: 100px;

    @media screen and (max-width: 1024px) {
        padding-top: 80px;
        padding-bottom: 60px;
    }

    @media screen and (max-width: 475px) {
        padding-top: 40px;
        padding-bottom: 40px;
    }
`;

const StyledBody = styled(Body)`
    white-space: pre-line;
`;


const FormBox = styled.div`
    width: 100%;
    flex: 1;

    margin-top: 60px;
    
    ${ButtonLink} {
        display: block;
        margin-left: auto;
    }
`;

const Input = styled.input`
    font-family: var(--font-poppins);
    font-size: var(--fs-form);
    line-height: 1.55;
    padding: 12px 18px;
    width: 100%;
    background-color: var(--color-white);
    border: 1px solid ${props => props?.$error ? 'var(--color-error)' : 'var(--color-label-stroke)'};
    outline: none;
    border-radius: 8px;
    max-height: 50px;

    &::placeholder {
        color: var(--color-light-400);
    }

    &:focus {
        border: 1px solid var(--color-green);
    }
`;

const Label = styled(Body).attrs({
    as: 'label'
})`
    font-weight: var(--fw-medium);
    font-size: var(--fs-form);
`;

const Required = styled.span`
    color: var(--color-green);
`;

const FormRow = styled.div`
    margin-bottom: 24px;
    
    &:last-of-type {
        margin-bottom: 36px;
    }
`;

const Error = styled(Body).attrs({
    $small: true,
})`
    color: var(--color-error);
    margin-top: 5px;
    font-size: var(--fs-error);
    
    ${props => props.$right && `
        text-align: right;
        margin-bottom: 24px;
        text-wrap-style: balance;       
    `}
`;

const ResetButton = styled(UnstyledButtonLink)`
    display: block;
    opacity: 0.5;
    font-size: 1.2rem;
    font-weight: var(--fw-semi-bold);
    margin: 12px 0 0 auto;
    
    &:hover {
        text-decoration: underline;
    }
`

const Wrapper = styled.div`
    position: relative;
    width: fit-content;
    margin-left: auto;
`

const SubmitButton = styled(Button)`
    position: relative;
    pointer-events: ${props => props.$loading ? 'none' : 'auto'};
    opacity: ${props => props.$loading ? 0.5 : 1};
`;

const StyledSpinner = styled(Spinner)`
    position: absolute;
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: scale(0.5);
    z-index: 1;
`

const initialState = { ok: null, message: null, errors: {} };

function Login() {
    const { appUser } = useAuth();
    const submitLoginFormWithAppUser = submitLoginForm.bind(null, appUser);
    const [state, submitAction, isPending] = useActionState(submitLoginFormWithAppUser, initialState);

    // Remember email across form validations
    const [email, setEmail] = useState("");

    useEffect(() => {
        if (!state?.ok) return;

        setEmail("");
        window.location.href = '/studenten';

    }, [state?.ok])

    // if (isAuthenticated) return <Navigate to="/studenten" replace />;

    return (
        <>
            <title>Inloggen &mdash; Jurist & Werk Congres</title>

            <Background>
                <StyledContainer>

                    <Flex>
                        <StyledHeadline2 $noTransform>Inloggen voor recruiters</StyledHeadline2>
                        <StyledBody $small>
                            Recruiters kunnen de documenten van alle studenten die dit jaar deelnemen inzien achter de inlog. Ook kunnen ze zien welke studenten geïnteresseerd zijn om in gesprek te gaan. Aanvullende (contact)informatie is te vinden in de app.
                        </StyledBody>

                        <FormBox>
                            <form action={submitAction}>
                                <FormRow>
                                    <Label htmlFor="email">E-mailadres<Required aria-label="required">*</Required></Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        name="email"
                                        placeholder="E-mailadres"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        $error={state?.errors?.email}
                                    />
                                    {state?.errors?.email?.map(error => <Error key={error}>{error}</Error>)}
                                </FormRow>

                                <FormRow>
                                    <Label htmlFor="password">Wachtwoord<Required aria-label="required">*</Required></Label>
                                    <Input id="password" type="password" name="password" placeholder="Wachtwoord" $error={state?.errors?.password} />
                                    {state?.errors?.password?.map(error => <Error key={error}>{error}</Error>)}

                                    <ResetButton to="/wachtwoord-resetten">
                                        Wachtwoord resetten
                                    </ResetButton>
                                </FormRow>

                                {!state?.ok && state?.message && <Error $right>{state?.message}</Error>}

                                <Wrapper>
                                    {isPending && <StyledSpinner $small $absolute /> }
                                    <SubmitButton as="button" type="submit" $loading={isPending}>
                                        Inloggen
                                    </SubmitButton>
                                </Wrapper>

                            </form>
                        </FormBox>
                    </Flex>
                </StyledContainer>
            </Background>
        </>
    );
}
export default Login;